import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import NavigationMenu from '../components/NavigationMenu';
import LogoBlanc from '../components/LogoBlanc';
import './ChambreEcoute2.css';

const ChambreEcoute2 = () => {
  // États React
  const [showPopup, setShowPopup] = useState(true);
  const [audioInitialized, setAudioInitialized] = useState(false);

  // Refs pour les éléments DOM et audio
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const trackRef = useRef(null);
  
  // Refs pour Three.js
  const rendererRef = useRef(null);
  const uniformsRef = useRef(null);

  // Initialisation de l'audio (fidèle à l'original)
  const initializeAudio = useCallback(async () => {
    console.log("🔊 initAudio appelé !");
    
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();

      // Resume du contexte si nécessaire
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      const reverbNode = audioContextRef.current.createConvolver();

      // Créer une impulse response simple pour la reverb
      const reverbBuffer = audioContextRef.current.createBuffer(
        2,
        0.5 * audioContextRef.current.sampleRate,
        audioContextRef.current.sampleRate
      );
      
      // Remplir le buffer avec du bruit pour la reverb
      for (let channel = 0; channel < reverbBuffer.numberOfChannels; channel++) {
        const channelData = reverbBuffer.getChannelData(channel);
        for (let i = 0; i < channelData.length; i++) {
          channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / channelData.length, 2);
        }
      }
      reverbNode.buffer = reverbBuffer;

      // Chemins possibles pour le fichier audio
      const audioPaths = [
        '/media/Sons/batonpluie.wav',
        '/batonpluie.wav',
        '/media/batonpluie.wav'
      ];

      for (const path of audioPaths) {
        try {
          console.log(`🔍 Tentative: ${path}`);
          const response = await fetch(path);
          
          if (response.ok) {
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
            
            const source = audioContextRef.current.createBufferSource();
            const gainNode = audioContextRef.current.createGain();
            const pannerNode = audioContextRef.current.createPanner();
            
            console.log("✅ Audio chargé :", path);

            source.buffer = audioBuffer;
            source.loop = true;

            // Paramètres du PannerNode (identiques à l'original)
            pannerNode.panningModel = "HRTF";
            pannerNode.distanceModel = "inverse";
            pannerNode.refDistance = 1;
            pannerNode.maxDistance = 5;
            pannerNode.rolloffFactor = 1;
            pannerNode.coneInnerAngle = 60;
            pannerNode.coneOuterAngle = 120;
            pannerNode.coneOuterGain = 0.3;

            // Chaîne audio (identique à l'original)
            source.connect(gainNode);
            gainNode.connect(pannerNode);
            pannerNode.connect(audioContextRef.current.destination);
            pannerNode.connect(reverbNode);
            reverbNode.connect(audioContextRef.current.destination);

            trackRef.current = { source, gainNode, pannerNode, reverbNode };

            gainNode.gain.value = 1; // Audio actif dès le début
            source.start();
            
            setAudioInitialized(true);
            console.log("🎵 Audio initialisé et démarré");
            break;
          }
        } catch (error) {
          console.warn(`⚠️ Échec: ${path} - ${error.message}`);
        }
      }

      // Si aucun fichier trouvé, créer un oscillateur de test
      if (!trackRef.current) {
        console.log('🔧 Création oscillateur de test');
        const oscillator = audioContextRef.current.createOscillator();
        const gainNode = audioContextRef.current.createGain();
        const pannerNode = audioContextRef.current.createPanner();
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(220, audioContextRef.current.currentTime);
        
        pannerNode.panningModel = "HRTF";
        pannerNode.distanceModel = "inverse";
        pannerNode.refDistance = 1;
        pannerNode.maxDistance = 5;
        pannerNode.rolloffFactor = 1;
        pannerNode.coneInnerAngle = 60;
        pannerNode.coneOuterAngle = 120;
        pannerNode.coneOuterGain = 0.3;
        
        oscillator.connect(gainNode);
        gainNode.connect(pannerNode);
        pannerNode.connect(audioContextRef.current.destination);
        pannerNode.connect(reverbNode);
        reverbNode.connect(audioContextRef.current.destination);
        
        gainNode.gain.value = 1;
        oscillator.start();
        
        trackRef.current = { source: oscillator, gainNode, pannerNode, reverbNode };
        setAudioInitialized(true);
      }
    }
  }, []);

  // Initialisation de Three.js (identique à l'original)
  const initializeThreeJS = useCallback(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    
    if (!container || !video) return;

    // Créer le canvas
    const canvas = document.createElement('canvas');
    canvas.id = 'webgl-canvas';
    canvas.style.position = 'absolute';
    canvas.style.pointerEvents = 'none';
    container.appendChild(canvas);

    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true });
    renderer.setSize(rect.width, rect.height);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const uniforms = {
      u_time: { value: 0.0 },
      u_mouse: { value: new THREE.Vector2(0.5, 0.5) },
      u_resolution: { value: new THREE.Vector2(rect.width, rect.height) },
      u_texture: { value: null },
    };
    uniformsRef.current = uniforms;

    // Démarrer la vidéo
    video.play().catch(e => console.warn("Erreur lecture vidéo:", e));

    // Créer la texture vidéo
    const videoTexture = new THREE.VideoTexture(video);
    videoTexture.minFilter = THREE.LinearFilter;
    videoTexture.magFilter = THREE.LinearFilter;
    videoTexture.format = THREE.RGBFormat;
    uniforms.u_texture.value = videoTexture;

    // Shader material (identique à l'original)
    const material = new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: `
        void main() {
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform float u_time;
        uniform vec2 u_mouse;
        uniform vec2 u_resolution;
        uniform sampler2D u_texture;

        void main() {
          vec2 uv = gl_FragCoord.xy / u_resolution.xy;
          vec2 mouse = u_mouse;

          float dist = distance(uv, mouse);
          float ripple = 0.2 * sin(30.0 * dist - u_time * 6.0) / (dist * 50.0 + 60.1);
          vec2 distortedUV = uv + normalize(uv - mouse) * ripple;

          gl_FragColor = texture2D(u_texture, distortedUV);
        }
      `,
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    // Animation loop
    const animate = () => {
      uniforms.u_time.value += 0.01;

      if (video.readyState >= video.HAVE_CURRENT_DATA) {
        videoTexture.needsUpdate = true;
      }

      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    };
    animate();

    // Gestionnaire resize
    const handleResize = () => {
      const newRect = container.getBoundingClientRect();
      canvas.width = newRect.width;
      canvas.height = newRect.height;
      renderer.setSize(newRect.width, newRect.height);
      uniforms.u_resolution.value.set(newRect.width, newRect.height);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Gestionnaire de mouvement souris (identique à l'original)
  const handleMouseMove = useCallback((event) => {
    // Effet Three.js
    if (uniformsRef.current && containerRef.current) {
      const bounds = containerRef.current.getBoundingClientRect();
      uniformsRef.current.u_mouse.value.set(
        (event.clientX - bounds.left) / bounds.width,
        1.0 - (event.clientY - bounds.top) / bounds.height
      );
    }

    // Audio spatial (identique à l'original)
    if (!audioContextRef.current || !trackRef.current) return;

    const track = trackRef.current;
    const yRatio = event.clientY / window.innerHeight;

    // Gestion de la reverb basée sur la position Y
    if (track.reverbNode) {
      if (yRatio > 0.9) {
        console.log("✨ Reverb activée");
        track.reverbNode.normalize = true;
      } else {
        track.reverbNode.normalize = false;
      }
    }

    // Spatialisation 3D
    const xPos = (event.clientX / window.innerWidth) * 2 - 1;
    const yPos = -((event.clientY / window.innerHeight) * 2 - 1);
    const zPos = 1 - Math.abs(yPos);

    const angleOffset = Math.abs(xPos) * 90;
    const outerGain = Math.max(0.1, 1 - Math.abs(xPos));

    if (track.pannerNode) {
      track.pannerNode.positionX.value = xPos;
      track.pannerNode.positionY.value = yPos;
      track.pannerNode.positionZ.value = zPos;
      track.pannerNode.coneOuterAngle = 60 + angleOffset;
      track.pannerNode.coneOuterGain = outerGain;
    }
  }, []);

  // Fermeture popup et initialisation
  const handleClosePopup = useCallback(async () => {
    console.log("🚀 Fermeture popup - Initialisation audio...");
    setShowPopup(false);
    await initializeAudio();
    initializeThreeJS();
  }, [initializeAudio, initializeThreeJS]);

  // Effects
  useEffect(() => {
    // Afficher la popup immédiatement au chargement
    console.log('🚀 Composant monté, popup devrait être visible');
    setShowPopup(true);
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  // Debug pour vérifier l'état de la popup
  console.log('🔍 État popup:', showPopup);

  return (
    <>
      <NavigationMenu />
      <LogoBlanc />
      
      {/* Popup d'autorisation audio (design original) - TOUJOURS VISIBLE EN DEBUG */}
      {showPopup ? (
        <div 
          className="popup" 
          onClick={handleClosePopup}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '20px',
            cursor: 'pointer',
            zIndex: 9999,
            flexDirection: 'column'
          }}
        >
          <div style={{ marginBottom: '20px' }}>
            Cliquez pour activer l'audio
          </div>
          <button 
            className="audiobtn" 
            data-index="0"
            style={{
              zIndex: 10000,
              margin: '10px',
              padding: '10px 20px',
              fontSize: '16px',
              background: 'transparent',
              color: 'white',
              border: '1px solid white',
              cursor: 'pointer'
            }}
          >
            #
          </button>
        </div>
      ) : null}

      {/* DEBUG: Bouton pour réafficher la popup */}
      {!showPopup && (
        <button
          onClick={() => {
            console.log('🔄 Réaffichage popup forcé');
            setShowPopup(true);
          }}
          style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            zIndex: 1000,
            padding: '10px',
            backgroundColor: 'red',
            color: 'white',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          POPUP
        </button>
      )}

      {/* Container principal */}
      <div className="chambre-ecoute">
        <div className="horizontal-scroll">
          <div className="section">
            <div className="grid grid1">
              
              {/* Zone des abysses (design original) */}
              <div className="abysses"></div>

              {/* Container vidéo */}
              <div ref={containerRef} className="image-container">
                <video 
                  ref={videoRef}
                  id="background-video"
                  autoPlay 
                  muted 
                  loop 
                  playsInline
                >
                  <source src="/media/FondSite.mp4" type="video/mp4" />
                  <source src="/FondSite.mp4" type="video/mp4" />
                </video>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChambreEcoute2;